import { defineStore } from "pinia";
import {
  getLeaderboard,
  getMyPeriod,
  getMyTotal,
  getMyUploads,
  getPeriodLeaderboard,
  getTotalLeaderboard,
} from "@/services/api/tokenomics";
import { utc8 } from "@/shared/utils/format";
import {
  createLeaderboardMetrics,
  leaderboardWindowLabel,
  shiftLeaderboardAnchor,
} from "@/features/leaderboard/model/view-model";
import { useAuthStore } from "@/stores/auth";

const DAY_MS = 24 * 60 * 60 * 1000;
const YEAR_CHUNK_DAYS = 90;

function addDays(dateString, days) {
  const date = new Date(`${dateString}T00:00:00.000Z`);
  date.setTime(date.getTime() + days * DAY_MS);
  return date.toISOString().slice(0, 10);
}

function yearUploadRanges(targetDate) {
  const ranges = [];
  let from = `${targetDate.slice(0, 4)}-01-01`;
  while (from <= targetDate) {
    const chunkEnd = addDays(from, YEAR_CHUNK_DAYS - 1);
    const to = chunkEnd < targetDate ? chunkEnd : targetDate;
    ranges.push({ from, to });
    from = addDays(to, 1);
  }
  return ranges;
}

// 当年只拉到今天，往年拉整年——热力图里今天之后的格子本来就是禁用的。
export function yearCoverageEnd(targetDate, today = utc8()) {
  const year = targetDate.slice(0, 4);
  return year === today.slice(0, 4) ? today : `${year}-12-31`;
}

export function yearUploadsKey(targetDate, provider, today = utc8()) {
  return `${targetDate.slice(0, 4)}:${provider}:${yearCoverageEnd(targetDate, today)}`;
}

export function mergeYearDay(yearItems, date, dayItems) {
  return [...yearItems.filter((item) => item.date !== date), ...dayItems.filter((item) => item.date === date)];
}

export const useLeaderboardStore = defineStore("leaderboard", {
  state: () => ({
    mode: "day",
    date: utc8(),
    provider: "all",
    window: null,
    items: [],
    myUploads: [],
    myYearUploads: null,
    myYearKey: null,
    myPeriod: null,
    myTotal: null,
    eligibleCount: 0,
    activeCount: 0,
    activeTokenTotal: 0,
    pageInfo: { limit: 100, offset: 0, hasNextPage: false },
    loading: false,
    error: null,
    requestId: 0,
  }),
  getters: {
    isToday: (state) => state.date === utc8(),
    isCurrentWindow(state) {
      if (state.mode === "total") return false;
      if (state.mode === "day") return state.date === utc8();
      return state.window?.status === "ongoing";
    },
    windowLabel(state) {
      if (state.mode === "total") return "已上传总量";
      if (state.mode === "day") return state.date;
      return leaderboardWindowLabel(state.window) || state.date;
    },
    metrics(state) {
      const auth = useAuthStore();
      return createLeaderboardMetrics({
        leaderboard: state.items,
        myUploads: state.myUploads,
        myYearUploads: state.myYearUploads,
        myPeriod: state.myPeriod,
        myTotal: state.myTotal,
        user: auth.user,
        date: state.date,
        mode: state.mode,
        eligibleCount: state.eligibleCount,
        activeCount: state.activeCount,
        activeTokenTotal: state.activeTokenTotal,
      });
    },
  },
  actions: {
    setMode(mode) {
      this.mode = mode;
      this.pageInfo.offset = 0;
      this.window = null;
    },
    setDate(date) {
      this.date = date;
      this.pageInfo.offset = 0;
      this.window = null;
    },
    setProvider(provider) {
      this.provider = provider;
      this.pageInfo.offset = 0;
    },
    moveWindow(direction) {
      if (this.mode === "total") return;
      this.setDate(shiftLeaderboardAnchor(this.date, this.mode, direction));
    },
    moveToCurrentWindow() {
      this.setDate(utc8());
    },
    async loadLeaderboard(
      targetDate = this.date,
      targetProvider = this.provider,
      targetMode = this.mode,
      targetOffset = 0,
      { refreshYear = false } = {}
    ) {
      const auth = useAuthStore();
      if (!auth.user) return;
      const requestId = ++this.requestId;
      this.loading = true;
      this.error = null;
      try {
        if (targetMode === "total") {
          const [boardResult, mineResult] = await Promise.allSettled([
            getTotalLeaderboard({ provider: targetProvider, limit: 100, offset: targetOffset }),
            getMyTotal({ provider: targetProvider }),
          ]);
          if (requestId !== this.requestId) return;
          this.myTotal = mineResult.status === "fulfilled" ? mineResult.value.item ?? null : null;
          this.myPeriod = null;
          this.myUploads = [];
          this.window = null;
          if (boardResult.status === "rejected") throw boardResult.reason;
          const board = boardResult.value;
          this.items = board.items ?? [];
          this.eligibleCount = board.eligibleCount ?? this.items.length;
          this.activeCount = board.activeCount ?? this.items.filter((item) => item.totalTokens > 0).length;
          this.activeTokenTotal =
            board.activeTokenTotal ?? this.items.reduce((sum, item) => sum + Math.max(item.totalTokens, 0), 0);
          this.pageInfo = board.pageInfo ?? { limit: 100, offset: targetOffset, hasNextPage: false };
          return;
        }

        if (targetMode !== "day") {
          const [boardResult, mineResult] = await Promise.allSettled([
            getPeriodLeaderboard({
              period: targetMode,
              anchor: targetDate,
              provider: targetProvider,
              limit: 100,
              offset: targetOffset,
            }),
            getMyPeriod({ period: targetMode, anchor: targetDate, provider: targetProvider }),
          ]);
          if (requestId !== this.requestId) return;
          this.myPeriod = mineResult.status === "fulfilled" ? mineResult.value.item ?? null : null;
          this.myTotal = null;
          this.myUploads = [];
          this.window = mineResult.status === "fulfilled" ? mineResult.value.window ?? null : null;
          if (boardResult.status === "rejected") throw boardResult.reason;
          const board = boardResult.value;
          this.items = board.items ?? [];
          this.eligibleCount = board.eligibleCount ?? this.items.length;
          this.activeCount = board.activeCount ?? this.items.filter((item) => item.totalTokens > 0).length;
          this.activeTokenTotal =
            board.activeTokenTotal ?? this.items.reduce((sum, item) => sum + Math.max(item.totalTokens, 0), 0);
          this.pageInfo = board.pageInfo ?? { limit: 100, offset: targetOffset, hasNextPage: false };
          this.window = board.window ?? this.window;
          return;
        }

        // 年度上传按「年 + provider + 覆盖到哪天」缓存：热力图点一天就切一天，不该每次重拉一整年；
        // 当天的数据每次都单独拉一份并合并进去，所以刚上传的用量照样能立刻显示。
        const yearKey = yearUploadsKey(targetDate, targetProvider);
        const reuseYear = !refreshYear && this.myYearKey === yearKey && Array.isArray(this.myYearUploads);
        const yearRanges = reuseYear ? [] : yearUploadRanges(yearCoverageEnd(targetDate));
        const [boardResult, mineResult, ...yearResults] = await Promise.allSettled([
          getLeaderboard({ date: targetDate, provider: targetProvider, limit: 100, offset: targetOffset }),
          getMyUploads({ from: targetDate, to: targetDate, provider: targetProvider }),
          ...yearRanges.map((range) => getMyUploads({ ...range, provider: targetProvider })),
        ]);
        if (requestId !== this.requestId) return;
        this.myUploads = mineResult.status === "fulfilled" ? mineResult.value.items ?? [] : [];
        const yearItems = reuseYear
          ? this.myYearUploads
          : yearResults.flatMap((result) => (result.status === "fulfilled" ? result.value.items ?? [] : []));
        this.myYearUploads = mergeYearDay(yearItems, targetDate, this.myUploads);
        this.myYearKey = reuseYear || yearResults.every((result) => result.status === "fulfilled") ? yearKey : null;
        this.myPeriod = null;
        this.myTotal = null;
        this.window = null;
        if (boardResult.status === "rejected") throw boardResult.reason;
        const board = boardResult.value;
        this.items = board.items ?? [];
        this.eligibleCount = board.eligibleCount ?? this.items.length;
        this.activeCount = board.activeCount ?? this.items.filter((item) => item.totalTokens > 0).length;
        this.activeTokenTotal =
          board.activeTokenTotal ?? this.items.reduce((sum, item) => sum + Math.max(item.totalTokens, 0), 0);
        this.pageInfo = board.pageInfo ?? { limit: 100, offset: targetOffset, hasNextPage: false };
      } catch (err) {
        if (requestId === this.requestId) {
          if (err?.payload?.error === "membership_unavailable") {
            this.items = [];
            this.eligibleCount = 0;
            this.activeCount = 0;
            this.activeTokenTotal = 0;
            this.pageInfo = { limit: 100, offset: targetOffset, hasNextPage: false };
          }
          this.error = err instanceof Error ? err.message : "加载失败";
        }
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
    async loadPage(offset) {
      return this.loadLeaderboard(this.date, this.provider, this.mode, Math.max(0, offset));
    },
    async refresh() {
      return this.loadLeaderboard(this.date, this.provider, this.mode, this.pageInfo.offset, { refreshYear: true });
    },
  },
});
